import { AlertTriangle, CalendarRange, CircleAlert, Info } from 'lucide-react'
import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router'
import {
	useMarkNoticeAsRead,
	useNoticeById,
} from '@/api/queries/notices-queries'
import { PageHeader } from '@/components/commons'
import {
	NoticeMedia,
	NoticeWatermark,
	useContentProtection,
} from '@/components/notices'
import { ROUTES } from '@/constants/routes'
import { cn } from '@/lib/utils'
import { useAuthStore } from '@/store'
import type { NoticeType } from '@/types/notice'

const TYPE_ICON: Record<NoticeType, typeof Info> = {
	info: Info,
	warning: AlertTriangle,
	danger: CircleAlert,
}

const TYPE_CLASS: Record<NoticeType, string> = {
	info: 'text-kaefer-sapphire-blue',
	warning: 'text-kaefer-honey-yellow',
	danger: 'text-red-600',
}

const PRIORITY_CLASS = {
	high: 'bg-red-100 text-red-700',
	medium: 'bg-yellow-100 text-yellow-700',
	low: 'bg-green-100 text-green-700',
} as const

const formatDay = (iso: string) => new Date(iso).toLocaleDateString('pt-BR')

export function NoticeDetailPage() {
	const { t } = useTranslation()
	const navigate = useNavigate()
	const { id = '' } = useParams()
	const user = useAuthStore((state) => state.user)

	const { data: notice, isLoading, isError } = useNoticeById(id)
	const { mutate: markNoticeAsRead } = useMarkNoticeAsRead()
	const markedRef = useRef(false)

	useContentProtection()

	useEffect(() => {
		if (!notice || notice.isRead || markedRef.current) return
		markedRef.current = true
		markNoticeAsRead(notice.id)
	}, [notice, markNoticeAsRead])

	const handleBack = () => {
		// idx > 0 indica que há uma entrada anterior dentro do app
		if ((window.history.state?.idx ?? 0) > 0) {
			navigate(-1)
			return
		}
		navigate(ROUTES.DASHBOARD.NOTICES)
	}

	if (isLoading) {
		return (
			<div className="px-4 py-6 sm:px-6">
				<PageHeader title={t('notices.detail.title')} onBack={handleBack} />
				<p className="py-16 text-center text-muted-foreground text-sm">
					{t('common.loading')}
				</p>
			</div>
		)
	}

	if (isError || !notice) {
		return (
			<div className="px-4 py-6 sm:px-6">
				<PageHeader title={t('notices.detail.title')} onBack={handleBack} />
				<p className="py-16 text-center text-muted-foreground text-sm">
					{t('notices.detail.notFound')}
				</p>
			</div>
		)
	}

	const Icon = TYPE_ICON[notice.type]
	const watermark = user ? <NoticeWatermark badge={user.badge} /> : null
	const period =
		notice.startsAt || notice.endsAt
			? `${notice.startsAt ? formatDay(notice.startsAt) : '—'} – ${notice.endsAt ? formatDay(notice.endsAt) : '—'}`
			: null

	return (
		<div className="px-4 py-6 sm:px-6">
			<PageHeader title={notice.title} onBack={handleBack} />

			<article className="mx-auto flex max-w-4xl flex-col gap-4 select-none [-webkit-touch-callout:none] [-webkit-tap-highlight-color:transparent] print:hidden">
				<div className="relative overflow-hidden rounded-lg border bg-background p-5">
					<div className="mb-4 flex flex-wrap items-center gap-2 text-xs">
						<Icon className={cn('size-5', TYPE_CLASS[notice.type])} />
						<span className="rounded-full bg-kaefer-dark-grey/10 px-2 py-0.5 font-semibold text-kaefer-dark-grey">
							{t(`administration.notificationsForm.type_${notice.type}`)}
						</span>
						<span
							className={cn(
								'rounded-full px-2 py-0.5 font-medium',
								PRIORITY_CLASS[notice.priority],
							)}
						>
							{t('notices.priority')}:{' '}
							{t(
								`administration.notificationsForm.priority_${notice.priority}`,
							)}
						</span>
						{period && (
							<span className="inline-flex items-center gap-1 text-muted-foreground">
								<CalendarRange className="size-3.5" />
								{period}
							</span>
						)}
					</div>
					<p className="whitespace-pre-wrap text-sm leading-relaxed">
						{notice.message}
					</p>
					{watermark}
				</div>

				{notice.media.length > 0 && (
					<NoticeMedia media={notice.media} overlay={watermark} />
				)}
			</article>
		</div>
	)
}
